<template>
    <div>
        <Dialog v-model:visible="visible" :style="{ width: '450px' }" header="Add Pass Criteria for your step" :modal="true" @hide="emit('hide-dialog', true)">
            <!-- Outcome type selection starts
                This part basically serves only for processing in front
                to adjust the form type to show to avoid possible user errors
            -->
            <div class="p-fluid formgrid grid">
                <div class="field col-12">
                    <label for="type" v-tooltip="'Select Pass criteria type'">Outcome Criteria Type:</label>

                    <Dropdown id="type" v-model="store.outcome.type" :options="types" optionLabel="label" optionValue="name" placeholder="Select the criteria type" />
                </div>
                <Divider />
                <div v-if="store.outcome.type === 'boolean' || store.outcome.type === 'value'" class="field col-12">
                    <label for="formula">Formula:</label>
                    <Dropdown id="formula" :class="{ 'p-invalid': submitted && !store.outcome.formula_id }" v-model="store.outcome.formula_id" :options="filteredFormulas" optionLabel="code" optionValue="id" placeholder="Select a formula" />
                    <small class="p-invalid" v-if="submitted && !store.outcome.formula_id">Formula is a required field.</small>
                </div>
                <div v-if="store.outcome.type === 'attribute'" class="field col-12">
                    <label for="attribute">Attribute:</label>
                    <!-- put here attributes later instead of filteredFormulas-->
                    <Dropdown
                        id="attribute"
                        :class="{ 'p-invalid': submitted && !store.outcome.attribute_code }"
                        v-model="store.outcome.attribute_code"
                        :options="filteredAttributes"
                        optionLabel="label"
                        optionValue="code"
                        placeholder="Select an attribute"
                        @change="checkAttributeType"
                    />
                    <small class="p-invalid" v-if="submitted && !store.outcome.attribute_code">Attribute is a required field.</small>
                </div>
                <div v-if="store.outcome.type === 'attribute' || store.outcome.type === 'value'" class="field col-6">
                    <label for="operator">Operator:</label>
                    <Dropdown id="operator" :class="{ 'p-invalid': submitted && !store.outcome.operator }" v-model="store.outcome.operator" :options="operators" optionLabel="label" optionValue="value" placeholder="Select an operator" />
                    <small class="p-invalid" v-if="submitted && !store.outcome.operator">Operator is a required field.</small>
                </div>
                <div v-if="store.outcome.type === 'attribute' || store.outcome.type === 'value'" class="field col-6">
                    <label for="value">Value:</label>
                    <Dropdown
                        v-if="optionsType"
                        id="value"
                        :class="{ 'p-invalid': submitted && !store.outcome.value }"
                        v-model="store.outcome.value"
                        :options="attributeOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select an option"
                        :showClear="true"
                    />
                    <InputNumber v-else v-model="store.outcome.value" :class="{ 'p-invalid': submitted && !store.outcome.value }" placeholder="Enter numeric value" />
                    <!-- <InputText v-model="store.outcome.value" :class="{ 'p-invalid': submitted && !store.outcome.value }" placeholder="Enter any value" v-tooltip="'Input numeric values for better results'" /> -->
                    <small class="p-invalid" v-if="submitted && !store.outcome.value">Value is a required field.</small>
                </div>
            </div>
            <template #footer>
                <Button label="Submit" @click="submit" />
            </template>
        </Dialog>
    </div>
</template>

<script setup>
import { ref, computed, watch, inject, defineEmits, defineProps, onUnmounted, onMounted } from 'vue';
import { useStepOutcomeStore } from '../store';
import { storeToRefs } from 'pinia';

const emit = defineEmits(['hide-dialog']);
const props = defineProps({
    mode: {
        type: String,
        default: 'add',
    },
});

const store = useStepOutcomeStore();
const { types, operators } = storeToRefs(store);

const stepStore = inject('stepStore');
const formulas = inject('formulas');

const visible = ref(true);
const submitted = ref(false);

const optionsType = ref(false);
const attributeOptions = ref([]);

onMounted(() => {
    if (store.outcome.attribute_code) {
        checkAttributeType();
    }
});

// filters out formula on the basis of outcome type selected
// basically filters out boolean or value based formula
const filteredFormulas = computed(() => {
    const formula_type = store.outcome.type === 'boolean' ? '1' : store.outcome.type === 'value' ? '2' : null;
    return formulas.value?.filter((formula) => formula.type === formula_type);
});

// list of all filtered attributes
const filteredAttributes = computed(() => {
    const attributes = stepStore.activeStep?.block?.form_attributes ?? stepStore.activeStep?.block?.attributes;
    return attributes?.filter((attribute) => [5, 8, 9, 10, 11].includes(parseInt(attribute.type)));
});

// generates the active currentOutcomeType so that everytime it changes
const currentOutcomeType = computed(() => {
    return store?.outcome?.type;
});

// watch for the outcome type changes to refresh the submitted value
// just in case outcome has been submitted with validation errors and user changes outcome type to avoid the errors shown
watch(currentOutcomeType, () => {
    // nullify the outcome keys where type doesnt matches
    switch (currentOutcomeType.value) {
        case 'boolean':
            store.outcome.attribute_code = null;
            store.outcome.operator = null;
            store.outcome.formula_id = null;
            break;

        case 'value':
            store.outcome.attribute_code = null;
            store.outcome.formula_id = null;
            break;

        case 'attribute':
            store.outcome.formula_id = null;
            break;
    }
    submitted.value = false;
});

const checkAttributeType = () => {
    const attributeCode = store.outcome.attribute_code;
    const attributeArr = stepStore.steps
        ?.filter((step) => step.block) //filter out ones null blocks
        ?.map((step) => step.block) // map out block from existing steps
        ?.map((block) => block.attributes)
        .flat(Infinity);
    const attributeDetails = attributeArr.find((attribute) => attribute.code === attributeCode);
    if ([8, 9, 10, 11].includes(parseInt(attributeDetails?.type))) {
        attributeOptions.value = attributeDetails.options;
        optionsType.value = true;
    } else {
        attributeOptions.value = [];
        optionsType.value = false;
    }
};

// actions
const submit = async () => {
    submitted.value = true;
    if (isValid()) {
        // remove the type from the outcome state to avoid any issues in the api
        // and then submit on the basis of its mode
        const success = props.mode === 'edit' ? await store.updateOutcome(store.outcome, stepStore.activeStep?.id, store.outcome.id) : await store.createOutcome(store.outcome, stepStore.activeStep?.id);
        if (success) {
            emit('hide-dialog', true);
        }
    }
};

// helpers

const isValid = () => {
    // step to is by default required in all
    switch (store.outcome.type) {
        case 'boolean':
            return !store.outcome.formula_id ? false : true;
        case 'value':
            return !store.outcome.formula_id && !store.outcome.operator && !store.outcome.value ? false : true;
        case 'attribute':
            return !store.outcome.attribute_code && !store.outcome.operator && !store.outcome.value ? false : true;
    }

    return true;
};

onUnmounted(() => {
    store.initOutcome();
});
</script>
